import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PartsTab, DevelopmentTab, WorksTab, QuoteTab } from "@/components/reader/ProductionTabs";
import { ZoomIn, ZoomOut, RotateCw, Check, Pencil, HelpCircle, Ban, X, Eye, Plus, ChevronLeft, ChevronRight } from "lucide-react";

const ORIGIN_BADGE = {
  EXPLICIT_TEXT: ["ESPLICITO", "bg-emerald-100 text-emerald-900"],
  EXPLICIT_ON_DRAWING: ["ESPLICITO", "bg-emerald-100 text-emerald-900"],
  DERIVED_GEOMETRY: ["DERIVATO", "bg-blue-100 text-blue-900"],
  VISIBLE_GEOMETRY: ["GEOMETRIA VISIBILE", "bg-amber-100 text-amber-900"],
  PROCESS_INFERENCE: ["DEDUZIONE DI PROCESSO", "bg-purple-100 text-purple-900"],
  UNKNOWN: ["SCONOSCIUTO", "bg-gray-200 text-gray-700"],
  NOT_SHOWN: ["NON MOSTRATO", "border border-gray-300 text-gray-500"],
  OPERATOR_REVIEWED: ["REVISIONATO", "bg-cyan-100 text-cyan-900"],
};
const STATE_LABEL = { CONFIRM: ["Confermato", "text-emerald-700"], CORRECT: ["Corretto", "text-cyan-700"],
  MARK_UNKNOWN: ["Sconosciuto", "text-gray-600"], NOT_APPLICABLE: ["N/A", "text-gray-500"],
  REJECT_FIELD: ["Rifiutato", "text-red-700"], ADD_MISSING: ["Aggiunto", "text-cyan-700"],
  REVIEW_REQUIRED: ["DA RIVEDERE (semantica aggiornata)", "text-amber-700"],
  SYSTEM_SEMANTIC_NORMALIZATION: ["Normalizzazione semantica", "text-blue-700"] };

const THREAD_RE = /^M\d+([xX]\d+([.,]\d+)?)?$/;
const holeLabel = (h, i) => {
  const d = String(nv(h.diameter) ?? "");
  const n = h.instances ?? 1;
  if (THREAD_RE.test(d)) return `Foro ${h.feature_id || i + 1}: ${n} fori filettati ${d}`;
  return `Foro ${h.feature_id || i + 1}: Ø${d} ×${n}`;
};

function OriginBadge({ node }) {
  const key = node?.origin || node?.state || (node?.value == null ? "UNKNOWN" : null) || "UNKNOWN";
  const [label, cls] = ORIGIN_BADGE[key] || [key, "bg-secondary"];
  return <span className={`px-1 py-0.5 text-[9px] ${cls}`}>{label}</span>;
}

const nv = (node) => (node && typeof node === "object" && "value" in node) ? node.value : node;
const bboxOf = (node) => node?.bbox_pct || null;
const pageOf = (node) => {
  if (node?.page) return node.page;
  const m = /P(\d+)_/.exec(node?.view || "");
  return m ? parseInt(m[1]) : 1;
};

function buildFields(parsed) {
  if (!parsed) return [];
  const df = parsed.drawing_facts || {}, pg = parsed.part_geometry || {}, mp = parsed.manufacturing_plan || {};
  const F = (sec, id, label, path, node) => ({ sec, id, label, path, node: node ?? null });
  const out = [];
  [["code", "Codice"], ["description", "Descrizione"], ["revision", "Revisione"], ["date", "Data"]]
    .forEach(([k, l]) => out.push(F("1. Riepilogo", `df-${k}`, l, `drawing_facts.${k}`, df[k])));
  (df.scales || []).forEach((s, i) => out.push(F("2. Cartiglio", `scale-${i}`, `Scala ${i + 1}`, `drawing_facts.scales[${i}]`, s)));
  out.push(F("2. Cartiglio", "units", "Unità", "drawing_facts.units", df.units));
  (df.tolerances || []).forEach((t, i) => out.push(F("2. Cartiglio", `tol-${i}`, `Tolleranza ${i + 1}`, `drawing_facts.tolerances[${i}]`, t)));
  out.push(F("3. Materiale e forma", "grade", "Materiale (lega/qualità)", "drawing_facts.material_grade", df.material_grade));
  out.push(F("3. Materiale e forma", "form", "Forma materiale", "drawing_facts.material_form", df.material_form));
  out.push(F("4. Spessore", "thickness", "Spessore", "drawing_facts.thickness", df.thickness));
  out.push(F("5. Finiture e trattamenti", "finish", "Finitura (globale)", "drawing_facts.finish", df.finish));
  (df.bom_rows || []).forEach((r, i) => {
    ["finish", "surface_treatment", "heat_treatment"].forEach((k) => {
      if (r[k]) out.push(F("5. Finiture e trattamenti", `bomfin-${i}-${k}`, `BOM pos ${r.position}: ${k}`, `drawing_facts.bom_rows[${i}].${k}`, { value: r[k], bbox_pct: r.bbox_pct }));
    });
  });
  out.push(F("6. Quantità e unità", "jobqty", "Quantità commessa (da RFQ/cliente)", "job_inputs.quantity", parsed.job_inputs?.quantity ?? { value: null, state: "UNKNOWN" }));
  const env = pg.finished_envelope || {};
  ["x", "y", "z"].forEach((a) => out.push(F("7. Envelope", `env-${a}`, `Envelope ${a.toUpperCase()}`, `part_geometry.finished_envelope.${a}`, env[a])));
  (df.bom_rows || []).forEach((r, i) => out.push(F("8. Componenti e distinta", `bom-${i}`,
    `BOM pos ${r.position ?? i + 1}: ${r.description ?? ""} ×${r.quantity ?? "?"}`, `drawing_facts.bom_rows[${i}]`,
    { value: r.visible_text, bbox_pct: r.bbox_pct, page: r.page })));
  (df.unresolved_bom_fields || []).forEach((u, i) => out.push(F("8. Componenti e distinta", `bomu-${i}`,
    `BOM unresolved (riga ${u.row_ref}, col ${u.column})`, `drawing_facts.unresolved_bom_fields[${i}]`,
    { value: u.visible_text, bbox_pct: u.bbox_pct })));
  (pg.holes || []).forEach((h, i) => out.push(F("9. Fori", `hole-${i}`,
    holeLabel(h, i), `part_geometry.holes[${i}].diameter`, h.diameter)));
  (pg.slot_features || []).forEach((s, i) => {
    out.push(F("10. Asole", `slot-${i}`, `Asola ${s.slot_id} (${s.slot_type}) L=${nv(s.overall_length) ?? "?"} W=${nv(s.width) ?? "?"} ${s.through_or_blind}`, `part_geometry.slot_features[${i}].overall_length`, s.overall_length));
    out.push(F("10. Asole", `slot-${i}-w`, `Asola ${s.slot_id}: larghezza`, `part_geometry.slot_features[${i}].width`, s.width));
  });
  (pg.bend_features || []).forEach((b, i) => out.push(F("11. Pieghe", `bend-${i}`,
    `${b.bend_id} ${b.bend_type} ${nv(b.final_angle_deg) ?? "?"}° asse=${nv(b.bend_axis_length) ?? "?"}`,
    `part_geometry.bend_features[${i}].final_angle_deg`, b.final_angle_deg)));
  out.push(F("11. Pieghe", "gbc", "Pieghe geometriche (conteggio)", "manufacturing_plan.geometric_bend_count", { value: mp.geometric_bend_count }));
  out.push(F("11. Pieghe", "epoc", "Operazioni pressa stimate", "manufacturing_plan.estimated_press_operation_count", { value: mp.estimated_press_operation_count }));
  (pg.roll_features || []).forEach((r, i) => out.push(F("12. Calandrature", `roll-${i}`,
    `${r.roll_id} ${r.geometry_type} OD=${nv(r.outside_diameter) ?? "?"} ${r.declared_process ? "(dichiarata)" : "(dedotta)"}`,
    `part_geometry.roll_features[${i}].outside_diameter`, r.outside_diameter)));
  (pg.weld_features || []).forEach((w, i) => out.push(F("13. Saldature", `weld-${i}`,
    `${w.weld_id} ${w.process} ${w.joint_type} ${w.weld_symbol || ""} (${w.declared_or_inferred})`,
    `part_geometry.weld_features[${i}].weld_size`, w.weld_size)));
  (mp.operations || []).forEach((o, i) => out.push(F(o.declared_by_drawing ? "14. Lavorazioni dichiarate" : "15. Lavorazioni dedotte",
    `op-${i}`, `${o.op_id || i} ${o.process}`, `manufacturing_plan.operations[${i}]`,
    { value: o.evidence_citation, origin: o.basis, bbox_pct: o.bbox_pct })));
  const jir = parsed.job_inputs_required;
  (Array.isArray(jir) ? jir : (jir?.items || jir?.inputs || [])).forEach?.((it, i) =>
    out.push(F("16. Informazioni mancanti", `jir-${i}`, typeof it === "string" ? it : (it?.name || it?.value || `Input ${i + 1}`),
      `job_inputs_required[${i}]`, typeof it === "object" ? it : { value: it })));
  (pg.unresolved_elements || []).forEach((u, i) => out.push(F("17. Anomalie e unresolved", `unres-${i}`,
    typeof u === "string" ? u : (u?.value || JSON.stringify(u).slice(0, 60)), `part_geometry.unresolved_elements[${i}]`,
    typeof u === "object" ? u : { value: u })));
  ((parsed.self_assessment || {}).critical_uncertainties || []).forEach((u, i) =>
    out.push(F("17. Anomalie e unresolved", `unc-${i}`, u, `self_assessment.critical_uncertainties[${i}]`, { value: u })));
  return out;
}

function ActionDialog({ open, onClose, field, mode, onSubmit }) {
  const [v, setV] = useState(""); const [unit, setUnit] = useState("mm");
  const [reason, setReason] = useState(""); const [src, setSrc] = useState("DRAWING");
  useEffect(() => { setV(""); setReason(""); setSrc(mode === "ADD_MISSING" ? "CUSTOMER_PROVIDED" : "DRAWING"); }, [open, mode]);
  if (!field) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-sm">
          {mode === "CORRECT" ? "Correggi" : "Aggiungi dato mancante"}: {field.label}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-xs">
          <div><Label className="text-xs">Nuovo valore *</Label>
            <Input data-testid="corr-value" value={v} onChange={(e) => setV(e.target.value)} /></div>
          <div><Label className="text-xs">Unità</Label>
            <Input data-testid="corr-unit" value={unit} onChange={(e) => setUnit(e.target.value)} /></div>
          <div><Label className="text-xs">Motivazione * </Label>
            <Textarea rows={2} data-testid="corr-reason" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
          <div><Label className="text-xs">Fonte del dato</Label>
            <Select value={src} onValueChange={setSrc}>
              <SelectTrigger data-testid="corr-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DRAWING">Visibile sul disegno</SelectItem>
                <SelectItem value="CUSTOMER_PROVIDED">Fornito dal cliente (esterno al disegno)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[9px] text-muted-foreground mt-1">Un dato fornito dal cliente NON diventa una lettura AI.</p></div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={onClose}>Annulla</Button>
            <Button size="sm" data-testid="corr-submit" onClick={() => onSubmit(v, unit, reason, src)}>Registra</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ReaderReview() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const canReview = ["owner", "admin", "estimator"].includes(user?.role);
  const [data, setData] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [rot, setRot] = useState(0);
  const [showOverlays, setShowOverlays] = useState(true);
  const [selected, setSelected] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [draft, setDraft] = useState(null);
  const [blockers, setBlockers] = useState(null);
  const fieldRefs = useRef({});

  const load = async () => {
    const { data: d } = await api.get(`/reader/jobs/${jobId}`);
    setData(d);
    if (["QUOTE_DRAFT_READY"].includes(d.job.status)) {
      try { const { data: q } = await api.get(`/reader/jobs/${jobId}/quote-draft`); setDraft(q); } catch { /* noop */ }
    }
  };
  useEffect(() => { load(); }, [jobId]);
  useEffect(() => {
    let url;
    api.get(`/reader/jobs/${jobId}/page-image?page=${page}&dpi=140`, { responseType: "blob" })
      .then((r) => { setPageCount(parseInt(r.headers["x-page-count"] || "1")); url = URL.createObjectURL(r.data); setImgUrl(url); })
      .catch(() => setImgUrl(null));
    return () => url && URL.revokeObjectURL(url);
  }, [jobId, page]);

  const parsed = data?.raw?.parsed;
  const fields = useMemo(() => buildFields(parsed), [parsed]);
  const states = useMemo(() => {
    const m = {};
    (data?.events || []).forEach((e) => { if (e.field_path) m[e.field_path] = e; });
    return m;
  }, [data]);
  const sections = useMemo(() => {
    const m = new Map();
    fields.forEach((f) => { if (!m.has(f.sec)) m.set(f.sec, []); m.get(f.sec).push(f); });
    return [...m.entries()].sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
  }, [fields]);
  const overlays = useMemo(() => fields.filter((f) => bboxOf(f.node) && pageOf(f.node) === page), [fields, page]);

  const sendEvent = async (field, action, extra = {}) => {
    try {
      await api.post(`/reader/jobs/${jobId}/events`, {
        action, field_path: field.path, field_label: field.label,
        old_value: nv(field.node), evidence: bboxOf(field.node) ? { page, bbox_pct: bboxOf(field.node) } : null, ...extra });
      toast.success(`${action} registrato (append-only)`); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const globalAction = async (path, ok) => {
    try { await api.post(`/reader/jobs/${jobId}/${path}`, {}); toast.success(ok); setBlockers(null); load(); }
    catch (e) {
      const det = e.response?.data?.detail;
      if (det?.blockers) { setBlockers(det.blockers); toast.error("Approvazione bloccata: unresolved critici"); }
      else toast.error(formatApiErrorDetail(det));
    }
  };
  const makeDraft = async () => {
    try { const { data: q } = await api.post(`/reader/jobs/${jobId}/quote-draft`); setDraft(q); toast.success("Bozza economica assistita creata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-8 text-sm">Caricamento…</div>;
  const job = data.job;
  const closed = ["HUMAN_APPROVED", "QUOTE_DRAFT_READY", "REJECTED"].includes(job.status);

  return (
    <div className="p-4" data-testid="reader-review-page">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h1 className="text-lg font-display font-bold">
            {job.is_dev_replay && <span className="text-[10px] bg-violet-100 text-violet-800 px-1 mr-2 align-middle">FROZEN_DEV_REPLAY — NOT A REAL RFQ</span>}
            Revisione lettura: {job.filename}
          </h1>
          <div className="text-[10px] text-muted-foreground">
            Stato <b data-testid="job-status">{job.status}</b>
            {job.substates?.includes("CRITICAL_REVIEW_ITEMS_PRESENT") && <span className="ml-1 px-1 bg-red-100 text-red-800 font-bold">CRITICAL_REVIEW_ITEMS_PRESENT</span>}
            <details className="inline-block ml-2 align-middle" data-testid="tech-section">
              <summary className="cursor-pointer inline text-muted-foreground hover:underline">Sezione tecnica (audit e raw)</summary>
              <span className="block mt-0.5">raw hash {data.raw?.raw_hash} · modello {data.raw?.model} · ${data.raw?.cost_usd} · resp {data.raw?.response_id} · req {data.raw?.x_request_id} · token {data.raw?.input_tokens}/{data.raw?.output_tokens} · eventi {data.events?.length} · raw AI IMMUTABILE, delta append-only</span>
            </details>
          </div>
        </div>
        {canReview && (
          <div className="flex gap-1.5">
            {!job.assigned_to && !closed && <Button size="sm" variant="outline" onClick={() => globalAction("claim", "Preso in carico")} data-testid="btn-claim">Prendi in carico</Button>}
            {job.assigned_to && !closed && <Button size="sm" variant="outline" onClick={() => globalAction("release", "Rilasciato")} data-testid="btn-release">Rilascia</Button>}
            {!closed && <Button size="sm" variant="outline" onClick={() => toast.success("Bozza revisione salvata (eventi già registrati append-only)")} data-testid="btn-save-draft">Salva bozza</Button>}
            {!closed && <Button size="sm" onClick={() => globalAction("approve", "Lettura approvata")} data-testid="btn-approve">Approva lettura</Button>}
            {closed && job.status !== "REJECTED" && <Button size="sm" variant="outline" onClick={() => globalAction("reopen", "Revisione riaperta")} data-testid="btn-reopen">Riapri revisione</Button>}
            {job.status === "HUMAN_APPROVED" && <Button size="sm" onClick={makeDraft} data-testid="btn-make-draft">Genera bozza economica</Button>}
            {!closed && <Button size="sm" variant="destructive" onClick={() => globalAction("reject", "Documento rifiutato")} data-testid="btn-reject-doc">Rifiuta documento</Button>}
          </div>
        )}
      </div>

      {blockers && (
        <div className="mb-3 border border-red-300 bg-red-50 p-2 text-xs" data-testid="approve-blockers">
          <b>Approvazione bloccata — unresolved critici:</b>
          {blockers.map((b) => <span key={b.path} className="ml-2 px-1 bg-red-100">{b.label}</span>)}
          <span className="ml-2 text-muted-foreground">Usa "Aggiungi dato" (es. quantità commessa fornita dal cliente).</span>
        </div>
      )}

      <Tabs defaultValue="lettura" className="w-full">
        <TabsList className="mb-2">
          <TabsTrigger value="lettura" data-testid="tab-lettura">1. Lettura</TabsTrigger>
          <TabsTrigger value="parti" data-testid="tab-parti">2. Parti/BOM</TabsTrigger>
          <TabsTrigger value="sviluppo" data-testid="tab-sviluppo">3. Sviluppo</TabsTrigger>
          <TabsTrigger value="lavorazioni" data-testid="tab-lavorazioni">4. Lavorazioni</TabsTrigger>
          <TabsTrigger value="preventivo" data-testid="tab-preventivo">5. Preventivo</TabsTrigger>
        </TabsList>

        <TabsContent value="lettura">
      <div className="grid grid-cols-2 gap-3" style={{ height: "calc(100vh - 230px)" }}>
        <div className="border border-border bg-white flex flex-col" data-testid="pdf-panel">
          <div className="flex items-center gap-1.5 border-b border-border p-1.5 text-xs">
            <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => setZoom((z) => Math.max(50, z - 25))} data-testid="zoom-out"><ZoomOut size={12} /></Button>
            <span>{zoom}%</span>
            <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => setZoom((z) => Math.min(300, z + 25))} data-testid="zoom-in"><ZoomIn size={12} /></Button>
            <Button size="sm" variant="outline" className="h-6 px-2" onClick={() => setRot((r) => (r + 90) % 360)} data-testid="rotate"><RotateCw size={12} /></Button>
            <span className="ml-2 font-bold" data-testid="page-label">Pagina {page} di {pageCount}</span>
            <Button size="sm" variant="outline" className="h-6 px-1.5" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev"><ChevronLeft size={12} /></Button>
            <Button size="sm" variant="outline" className="h-6 px-1.5" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)} data-testid="page-next"><ChevronRight size={12} /></Button>
            <Select value={String(page)} onValueChange={(v) => setPage(parseInt(v))}>
              <SelectTrigger className="h-6 w-16" data-testid="page-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Array.from({ length: pageCount }, (_, i) => <SelectItem key={i} value={String(i + 1)}>{i + 1}</SelectItem>)}</SelectContent>
            </Select>
            <label className="ml-auto flex items-center gap-1.5">
              <Switch checked={showOverlays} onCheckedChange={setShowOverlays} data-testid="toggle-overlays" />
              Overlay evidenze ({overlays.length})
            </label>
          </div>
          <div className="overflow-auto flex-1 relative">
            <div className="relative origin-top-left" style={{ width: `${zoom}%`, transform: `rotate(${rot}deg)` }}>
              {imgUrl ? <img src={imgUrl} alt="disegno" className="w-full block" data-testid="pdf-image" /> :
                <div className="p-8 text-xs text-muted-foreground">PDF non disponibile</div>}
              {showOverlays && imgUrl && overlays.map((f) => {
                const b = bboxOf(f.node);
                const sel = selected === f.id;
                return (
                  <div key={f.id} title={f.label} data-testid={`overlay-${f.id}`}
                    onClick={() => { setSelected(f.id); fieldRefs.current[f.id]?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                    className={`absolute cursor-pointer border-2 ${sel ? "border-red-500 bg-red-500/20 z-10" : "border-cyan-500/70 bg-cyan-400/10 hover:bg-cyan-400/25"}`}
                    style={{ left: `${b.x0}%`, top: `${b.y0}%`, width: `${b.x1 - b.x0}%`, height: `${b.y1 - b.y0}%` }} />
                );
              })}
            </div>
          </div>
        </div>

        <div className="border border-border bg-white overflow-y-auto" data-testid="fields-panel">
          {sections.map(([sec, fs]) => (
            <div key={sec} className="border-b border-border">
              <div className="px-3 py-1.5 bg-muted text-xs font-display font-bold sticky top-0 z-10">{sec} <span className="text-muted-foreground font-normal">({fs.length})</span></div>
              {fs.map((f) => {
                const ev = states[f.path];
                const sel = selected === f.id;
                return (
                  <div key={f.id} ref={(el) => (fieldRefs.current[f.id] = el)}
                    className={`px-3 py-1.5 border-b border-border/50 text-xs ${sel ? "bg-red-50 ring-1 ring-red-300" : ""}`}
                    data-testid={`field-${f.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button className="font-medium text-left hover:underline" data-testid={`field-label-${f.id}`}
                        onClick={() => setSelected(f.id)}>{f.label}</button>
                      <OriginBadge node={f.node} />
                      {f.node?.confidence && <span className="text-[9px] text-muted-foreground">conf {f.node.confidence}</span>}
                      {ev && <span className={`text-[9px] font-bold ${(STATE_LABEL[ev.action] || [])[1]}`} data-testid={`state-${f.id}`}>
                        ● {(STATE_LABEL[ev.action] || [ev.action])[0]}{ev.action === "CORRECT" ? `: ${ev.new_value}` : ""}</span>}
                    </div>
                    <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                      <span data-testid={`value-${f.id}`}>valore: <b className="text-foreground">{String(nv(f.node) ?? "—")}</b>{f.node?.unit ? ` ${f.node.unit}` : ""}</span>
                      {f.node?.view && <span>vista: {f.node.view}</span>}
                      {f.node?.evidence_text && <span>testo: "{String(f.node.evidence_text).slice(0, 40)}"</span>}
                      {f.node?.formula && <span>formula: {f.node.formula}</span>}
                      {f.node?.source_refs?.length > 0 && <span>fonti: {f.node.source_refs.slice(0, 3).join(",")}</span>}
                      {f.node?.alternatives?.length > 0 && <span>alt: {f.node.alternatives.slice(0, 2).join(" | ")}</span>}
                    </div>
                    {canReview && !closed && (
                      <div className="flex gap-1 mt-1">
                        <button title="Conferma" className="p-0.5 border border-border hover:bg-emerald-50" data-testid={`confirm-${f.id}`}
                          onClick={() => sendEvent(f, "CONFIRM")}><Check size={11} /></button>
                        <button title="Correggi" className="p-0.5 border border-border hover:bg-cyan-50" data-testid={`correct-${f.id}`}
                          onClick={() => setDialog({ field: f, mode: "CORRECT" })}><Pencil size={11} /></button>
                        <button title="Segna sconosciuto" className="p-0.5 border border-border" data-testid={`unknown-${f.id}`}
                          onClick={() => sendEvent(f, "MARK_UNKNOWN", { reason: "UNKNOWN confermato dall'operatore" })}><HelpCircle size={11} /></button>
                        <button title="Non applicabile" className="p-0.5 border border-border" data-testid={`na-${f.id}`}
                          onClick={() => sendEvent(f, "NOT_APPLICABLE", { reason: "Non applicabile" })}><Ban size={11} /></button>
                        <button title="Rifiuta" className="p-0.5 border border-border hover:bg-red-50" data-testid={`rejectf-${f.id}`}
                          onClick={() => sendEvent(f, "REJECT_FIELD", { reason: "Campo rifiutato" })}><X size={11} /></button>
                        {bboxOf(f.node) && <button title="Apri evidenza" className="p-0.5 border border-border hover:bg-amber-50" data-testid={`evidence-${f.id}`}
                          onClick={() => { setSelected(f.id); if (f.node.page && f.node.page !== page) setPage(f.node.page); }}><Eye size={11} /></button>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {canReview && !closed && (
            <div className="p-3">
              <Button size="sm" variant="outline" data-testid="btn-add-quantity"
                onClick={() => setDialog({ field: { id: "jobqty", label: "Quantità commessa", path: "job_inputs.quantity", node: null }, mode: "ADD_MISSING" })}>
                <Plus size={12} className="mr-1" />Aggiungi dato mancante (es. quantità commessa)</Button>
            </div>
          )}
        </div>
      </div>
        </TabsContent>
        <TabsContent value="parti" className="border border-border bg-white" style={{ maxHeight: "calc(100vh - 230px)", overflowY: "auto" }}>
          <PartsTab jobId={jobId} />
        </TabsContent>
        <TabsContent value="sviluppo" className="border border-border bg-white" style={{ maxHeight: "calc(100vh - 230px)", overflowY: "auto" }}>
          <DevelopmentTab jobId={jobId} />
        </TabsContent>
        <TabsContent value="lavorazioni" className="border border-border bg-white" style={{ maxHeight: "calc(100vh - 230px)", overflowY: "auto" }}>
          <WorksTab jobId={jobId} />
        </TabsContent>
        <TabsContent value="preventivo" className="border border-border bg-white" style={{ maxHeight: "calc(100vh - 230px)", overflowY: "auto" }}>
          <QuoteTab jobId={jobId} draft={draft} job={job} canReview={canReview} onMakeDraft={makeDraft} />
        </TabsContent>
      </Tabs>

      <ActionDialog open={!!dialog} onClose={() => setDialog(null)} field={dialog?.field} mode={dialog?.mode}
        onSubmit={async (v, unit, reason, src) => {
          await sendEvent(dialog.field, dialog.mode, { new_value: v, unit, reason, data_source: src });
          setDialog(null);
        }} />
    </div>
  );
}
